import React, { useEffect, useState } from 'react'
import { AuthProvider, AuthContainer, useAuth } from '@hssoft/auth-web-shared-lib'
import { useTranslation } from 'react-i18next'
import NoAccessModal from './NoAccessModal'
import { setUserLanguage } from 'i18n/userLanguage'
import { apolloStorage } from 'config/storage'
import { checkAccess } from './utils'
/* mark-only variant — the full logo carries a white wordmark that vanishes on the white card */
import logoSignin from 'media/icons/logo-signin-mark.svg?url'
import { Spinner } from 'media/icons'
import { LoadingWrapper } from 'style/styled'
import { version } from '../../../package.json'
export { isAdmin, checkAccess } from './utils'

const REQUIRED_ROLES = ['admin', 'company']

/* the app i18next uses legacy language keys while the lib speaks BCP-47 locales */
const APP_LANGUAGE_TO_LIB = { German: 'de_DE', English: 'en_GB', French: 'fr_FR', Italian: 'it_IT' }
const LIB_LOCALE_TO_APP = { de_DE: 'German', en_GB: 'English', fr_FR: 'French', it_IT: 'Italian' }

const AuthGate = ({ App, onNoAccess }) => {
	const { user, signOut } = useAuth()
	const currentUserHasAccess = user ? checkAccess(user) : null

	useEffect(() => {
		if (currentUserHasAccess === false) {
			/* no dashboard entitlement (custom:access_rights) — sign out so a refresh
			   lands on the login screen; the reason is surfaced by the wrapper's modal */
			onNoAccess()
			signOut().catch(() => {})
		}
	}, [currentUserHasAccess]) // eslint-disable-line react-hooks/exhaustive-deps

	if (currentUserHasAccess !== true) {
		return (
			<LoadingWrapper>
				<Spinner />
			</LoadingWrapper>
		)
	}
	return <App authData={user} signOut={signOut} />
}

export default AppComponent => () => {
	const [t, i18n] = useTranslation()
	const [showNoAccess, setShowNoAccess] = useState(false)
	return (
		<AuthProvider requiredRole={REQUIRED_ROLES} locale={APP_LANGUAGE_TO_LIB[i18n.language] || 'de_DE'}>
			{/* lives outside AuthContainer so it survives the sign-out */}
			<NoAccessModal open={showNoAccess} onClose={() => setShowNoAccess(false)} />
			<AuthContainer
				hideSignUp
				appVersion={version}
				loadingComponent={
					<LoadingWrapper>
						<Spinner />
					</LoadingWrapper>
				}
				onSignedOut={() => {
					/* also fires for passive sign-outs (session expiry, another tab) */
					apolloStorage.clear().catch(() => {})
				}}
				mfaChallengeFormProps={{ enableDeviceTrust: true }}
				signInFormProps={{
					logoSrc: logoSignin,
					/* the lib's default title welcomes to Console */
					labels: { title: t('auth_screen.message.welcome') }
				}}
				languageProps={{
					selectedLanguage: APP_LANGUAGE_TO_LIB[i18n.language],
					onLanguageChange: locale => {
						const language = LIB_LOCALE_TO_APP[locale] || 'German'
						/* persist as the per-user override so the choice survives reloads
						   and is not overridden by the company language after sign-in */
						setUserLanguage(language)
						i18n.changeLanguage(language)
					}
				}}
			>
				<AuthGate App={AppComponent} onNoAccess={() => setShowNoAccess(true)} />
			</AuthContainer>
		</AuthProvider>
	)
}
